import { useState, useRef, useEffect, useCallback } from "react";

import {
  CaptureGate,
  GATE_ARMED,
} from "@/features/tutor/vo-demo/audio/captureGate";

const STOP_TAIL_MS = 500; // extra tail after button release to capture last word
const STOP_FALLBACK_MS = 1000; // max wait for final_chunk_sent before force-resolving

type WorkletMessage = {
  type: string;
  buffer?: ArrayBuffer;
  capture_id?: number | null;
};

function encodeChunkB64(msg: WorkletMessage): string | null {
  if (!msg.buffer) return null;
  const bytes = new Uint8Array(msg.buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++)
    binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

type UseSpaceMicOptions = {
  /** Called with each base64-encoded Int16 PCM chunk (16kHz mono). */
  onChunk: (b64: string) => void;
  /** When false, the hook is a no-op. Defaults to true. */
  enabled?: boolean;
};

type UseSpaceMicReturn = {
  /** Resolves false when capture could not start — the caller must then
   *  finalize the voice turn it already opened. */
  startRecording: () => Promise<boolean>;
  stopRecording: () => Promise<void>;
  /** Optimistic capture on key-down: buffer locally before tap-vs-hold is known. */
  armRecording: () => Promise<void>;
  /** Tap: discard the pre-roll buffer. Nothing was sent while armed. */
  abortArmedRecording: () => void;
  isRecording: boolean;
  /** Armed but no turn open yet — drives the same "listening" UI as isRecording. */
  isArming: boolean;
};

/**
 * Minimal mic capture hook for the space chat panel.
 * Reuses the existing AudioWorklet at /audio/audio-processor.worklet.js
 * (downsamples 48kHz → 16kHz, converts Float32 → Int16 PCM).
 *
 * No store or RuntimeContext coupling — communicates only via onChunk callback.
 */
export function useSpaceMic({
  onChunk,
  enabled = true,
}: UseSpaceMicOptions): UseSpaceMicReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isArming, setIsArming] = useState(false);

  // Buffers audio locally while armed (key down, turn not open yet); flushes
  // into onChunk on commit; discards on a tap. See captureGate.js.
  const gateRef = useRef<CaptureGate | null>(null);
  if (gateRef.current === null) gateRef.current = new CaptureGate();
  // Echoed by the worklet in final_chunk_sent so a stale final from a capture
  // that was stopped and immediately superseded can't end the new one.
  const captureIdRef = useRef(0);

  // All audio resources created once on mount and reused across recordings.
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isInitializedRef = useRef(false);
  const isInitializingRef = useRef(false);
  const stopDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isCapturingRef = useRef(false);
  const stopResolveRef = useRef<(() => void) | null>(null);
  const onChunkRef = useRef(onChunk);
  onChunkRef.current = onChunk;

  const initAudio = useCallback(async () => {
    if (isInitializingRef.current) return;
    isInitializingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const audioContext = new AudioContext({ sampleRate: 48000 });
      audioContextRef.current = audioContext;

      await audioContext.audioWorklet.addModule(
        "/audio/audio-processor.worklet.js",
      );

      const workletNode = new AudioWorkletNode(audioContext, "audio-processor");
      workletNodeRef.current = workletNode;

      const emitChunkB64 = (msg: WorkletMessage) => {
        const b64 = encodeChunkB64(msg);
        if (b64 !== null) onChunkRef.current(b64);
      };

      workletNode.port.onmessage = (event) => {
        const msg = event.data as WorkletMessage;
        if (msg.type === "final_chunk_sent") {
          // A final from a superseded capture (worklet re-started after this
          // stop) must not end the current one. Undefined/null means a legacy
          // cached worklet — let it through rather than hanging the stop.
          if (msg.capture_id != null && msg.capture_id !== captureIdRef.current)
            return;
          // Only a LIVE turn may finalize. The capture-id check alone is not
          // enough: the id is bumped at the END of the async arm, so an aborted
          // capture's final still matches during the abort -> re-arm window,
          // and resetting here would wipe the freshly armed pre-roll.
          if (!gateRef.current?.onFinalChunk()) return;
          // Worklet flushed its last chunk — safe to send AUDIO_DONE now
          isCapturingRef.current = false;
          stopResolveRef.current?.();
          stopResolveRef.current = null;
          return;
        }
        if (msg.type !== "audio" || !msg.buffer) return;
        // Armed: buffered locally until the turn opens. Live: sent. Idle: a
        // stale flush from an aborted arm — dropped.
        gateRef.current?.onAudioChunk(msg, emitChunkB64);
      };

      const source = audioContext.createMediaStreamSource(stream);

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__micAnalyserNode = analyser;

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);

      stream.getTracks().forEach((t) => {
        t.onended = () => {
          isInitializedRef.current = false;
        };
      });

      isInitializedRef.current = true;
    } catch (err) {
      console.error("[useSpaceMic] init failed:", err);
    } finally {
      isInitializingRef.current = false;
    }
  }, []);

  const cleanupAudio = useCallback(() => {
    gateRef.current?.reset();
    setIsArming(false);
    workletNodeRef.current?.port.postMessage({ type: "stop" });
    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    isInitializedRef.current = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__micAnalyserNode = null;
  }, []);

  /**
   * Make sure the audio graph is usable, rebuilding it if it has gone stale
   * (device unplugged, context suspended, worklet torn down).
   *
   * Returns a reason, not a boolean: both callers run after their turn is
   * already open, so a bare `if (!ok) return` would be wrong — "not ready"
   * means different things depending on what has been committed.
   */
  const ensureCaptureReady = useCallback(async (): Promise<
    "ready" | "unavailable"
  > => {
    const hasLiveTrack = streamRef.current
      ? streamRef.current.getTracks().some((t) => t.readyState === "live")
      : false;
    const contextState = audioContextRef.current?.state;
    const contextNotRunning =
      contextState !== undefined && contextState !== "running";

    if (
      !isInitializedRef.current ||
      !hasLiveTrack ||
      contextNotRunning ||
      !workletNodeRef.current
    ) {
      cleanupAudio();
      await initAudio();
    }

    if (
      !audioContextRef.current ||
      !workletNodeRef.current ||
      !isInitializedRef.current
    ) {
      return "unavailable";
    }
    if (audioContextRef.current.state === "suspended") {
      await audioContextRef.current.resume();
    }
    return "ready";
  }, [initAudio, cleanupAudio]);

  // Eagerly initialize on mount so startRecording has zero setup latency.
  useEffect(() => {
    if (!enabled) return;
    initAudio();
    return () => {
      if (stopDebounceTimerRef.current) {
        clearTimeout(stopDebounceTimerRef.current);
        stopDebounceTimerRef.current = null;
      }
      cleanupAudio();
    };
  }, [enabled, initAudio, cleanupAudio]);

  const startRecording = useCallback(async (): Promise<boolean> => {
    if (!enabled) return false;

    // Cancel any pending debounced stop so the recording continues.
    // Also resolve any pending stopRecording promise so the caller isn't left hanging.
    if (stopDebounceTimerRef.current) {
      clearTimeout(stopDebounceTimerRef.current);
      stopDebounceTimerRef.current = null;
    }
    if (stopResolveRef.current) {
      stopResolveRef.current();
      stopResolveRef.current = null;
    }

    // Armed fast-path: capture has been running since key-down. The caller has
    // just opened the voice turn (startVoiceTurn), so flush the pre-roll into
    // it and go live. Skips the readiness dance — the graph was validated at
    // arm time, and a cleanup here would destroy the running capture.
    if (gateRef.current?.state === GATE_ARMED) {
      const flushed = gateRef.current.commit((msg: WorkletMessage) => {
        const b64 = encodeChunkB64(msg);
        if (b64 !== null) onChunkRef.current(b64);
      });
      isCapturingRef.current = true;
      setIsArming(false);
      setIsRecording(true);
      if (flushed > 0)
        console.debug(`[useSpaceMic] flushed ${flushed} pre-roll chunk(s)`);
      return true;
    }

    try {
      if ((await ensureCaptureReady()) !== "ready") {
        // The caller already opened a voice turn (startVoiceTurn runs before
        // startRecording in every caller), so a bare return would strand that
        // turn: no audio and nothing to finalize it. Report the failure so the
        // caller can close it.
        console.error("[useSpaceMic] startRecording: audio graph unavailable");
        gateRef.current?.reset();
        isCapturingRef.current = false;
        setIsRecording(false);
        return false;
      }
      captureIdRef.current += 1;
      workletNodeRef.current?.port.postMessage({
        type: "start",
        captureId: captureIdRef.current,
      });
      // No pre-roll on this path — go straight live so chunks pass through.
      gateRef.current?.commit(() => {});
      isCapturingRef.current = true;
      setIsRecording(true);
      return true;
    } catch (err) {
      console.error("[useSpaceMic] startRecording failed:", err);
      gateRef.current?.reset();
      isCapturingRef.current = false;
      setIsRecording(false);
      return false;
    }
  }, [enabled, ensureCaptureReady]);

  /**
   * Optimistic capture on key-down: start the worklet and buffer chunks
   * locally, before tap-vs-hold is known. A hold commits the buffer via
   * startRecording; a tap discards it via abortArmedRecording. Nothing is
   * sent to the gateway while armed.
   */
  const armRecording = useCallback(async () => {
    if (!enabled) return;
    if (isCapturingRef.current) return;
    if (gateRef.current?.state === GATE_ARMED) return;
    // A stop tail is still draining — let the threshold path's legacy start
    // take over (it cancels the tail and restarts cleanly).
    if (stopDebounceTimerRef.current || stopResolveRef.current) return;

    gateRef.current?.arm();
    setIsArming(true);
    try {
      const ready = await ensureCaptureReady();
      const stillArmed = gateRef.current?.state === GATE_ARMED;
      if (ready !== "ready") {
        // Two meanings, which is why ensureCaptureReady reports rather than
        // decides. Still armed: no turn exists, so just drop the pre-roll. No
        // longer armed: a press committed during warm-up (cold init can
        // outlast the threshold), so a turn IS open and must be failed.
        if (stillArmed) {
          gateRef.current?.abort();
        } else {
          isCapturingRef.current = false;
          setIsRecording(false);
          gateRef.current?.reset();
        }
        setIsArming(false);
        return;
      }
      // Aborted (quick tap) while the graph was warming up — nothing to start.
      // Committed-and-still-held: the start below is the one the commit skipped.
      // Committed-and-already-released: the release ran its stop against a
      // worklet that never started, so starting now would capture after the key
      // is up with nothing left to end it.
      const pressEnded =
        stopDebounceTimerRef.current !== null ||
        stopResolveRef.current !== null;
      if (!stillArmed && (!isCapturingRef.current || pressEnded)) {
        gateRef.current?.reset();
        setIsArming(false);
        return;
      }
      captureIdRef.current += 1;
      workletNodeRef.current?.port.postMessage({
        type: "start",
        captureId: captureIdRef.current,
      });
    } catch (err) {
      console.error("[useSpaceMic] armRecording failed:", err);
      // abort() is a no-op once the gate is LIVE, so a throw after a press had
      // already committed needs the recording failed, not merely aborted.
      if (gateRef.current?.state !== GATE_ARMED) {
        isCapturingRef.current = false;
        setIsRecording(false);
      }
      gateRef.current?.reset();
      setIsArming(false);
    }
  }, [enabled, ensureCaptureReady]);

  /** Tap: the turn never opened — discard the pre-roll and stop the worklet. */
  const abortArmedRecording = useCallback(() => {
    if (!gateRef.current?.abort()) return;
    setIsArming(false);
    workletNodeRef.current?.port.postMessage({ type: "stop" });
  }, []);

  const stopRecording = useCallback((): Promise<void> => {
    setIsRecording(false);
    if (stopDebounceTimerRef.current) {
      clearTimeout(stopDebounceTimerRef.current);
      stopDebounceTimerRef.current = null;
    }
    // 500ms tail so the last word isn't clipped if the user released the button
    // slightly early. After that, tell worklet to flush and wait for final_chunk_sent
    // before resolving — that's when it's safe to send CANVAS_AUDIO_DONE.
    return new Promise<void>((resolve) => {
      stopResolveRef.current = resolve;
      stopDebounceTimerRef.current = setTimeout(() => {
        stopDebounceTimerRef.current = null;
        workletNodeRef.current?.port.postMessage({ type: "stop" });
        // Fallback: resolve after STOP_FALLBACK_MS if worklet never sends final_chunk_sent
        stopDebounceTimerRef.current = setTimeout(() => {
          stopDebounceTimerRef.current = null;
          isCapturingRef.current = false;
          stopResolveRef.current = null;
          resolve();
        }, STOP_FALLBACK_MS);
      }, STOP_TAIL_MS);
    });
  }, []);

  return {
    startRecording,
    stopRecording,
    armRecording,
    abortArmedRecording,
    isRecording,
    isArming,
  };
}
